import {
  keepPreviousData,
  useMutation,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';

import type { ApiError } from '../api/client';
import { promptsApi, type PromptsListParams } from '../api/endpoints';
import type {
  Id,
  OkResponse,
  Page,
  Prompt,
  PromptCreateRequest,
  PromptUpdateRequest,
} from '../types/api';
import { queryKeys } from './keys';

export function usePrompts(params: PromptsListParams = {}): UseQueryResult<Page<Prompt>, ApiError> {
  return useQuery<Page<Prompt>, ApiError>({
    queryKey: queryKeys.prompts(params),
    queryFn: () => promptsApi.list(params),
    placeholderData: keepPreviousData,
    staleTime: 60_000,
  });
}

export function useCreatePrompt() {
  const qc = useQueryClient();
  return useMutation<Prompt, ApiError, PromptCreateRequest>({
    mutationFn: (payload) => promptsApi.create(payload),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.promptsRoot });
    },
  });
}

export function useUpdatePrompt() {
  const qc = useQueryClient();
  return useMutation<Prompt, ApiError, { promptId: Id; payload: PromptUpdateRequest }>({
    mutationFn: ({ promptId, payload }) => promptsApi.update(promptId, payload),
    onSuccess: (prompt) => {
      qc.setQueriesData<Page<Prompt>>({ queryKey: queryKeys.promptsRoot }, (page) =>
        page
          ? { ...page, items: page.items.map((item) => (item.id === prompt.id ? prompt : item)) }
          : page,
      );
    },
  });
}

export function useDeletePrompt() {
  const qc = useQueryClient();
  return useMutation<OkResponse, ApiError, Id>({
    mutationFn: (promptId) => promptsApi.remove(promptId),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: queryKeys.promptsRoot });
    },
  });
}
